import { QueryKey } from '@/enums';
import { TenantEntity, TwoFactorAuthentication } from '@/interfaces';
import { newQueryClient } from '@/queries/reactQueryConfig';
import { MagicLoginPayload, MagicLoginResponse, SignInPayload } from '@/types';
import { clearAuthStorage, getToken, getTokens } from '@/utils';
import axios, { AxiosResponse } from 'axios';
import { get } from 'lodash';
import APIService from './base.service';
import { isSsoSession, withSsoSession } from './sso-session';

const baseUrl = process.env.REACT_APP_BACKEND_API_URL;

// An SSO session has no token to revoke: ending it means the API dropping
// the session and its cookie. The Amili SSO session itself stays signed in.
const postSignOut = () =>
  isSsoSession()
    ? axios.post(baseUrl + '/auth/logout', {}, withSsoSession({ headers: {} }))
    : axios.post(baseUrl + '/auth/sign-out', {
        accessToken: getToken('accessToken'),
      });

export class AuthService {
  static getRoles() {
    return APIService.get('/roles');
  }

  static getTwoFactorAuthenticationQRCode(payload: {
    accessToken: string;
    email: string;
  }): Promise<AxiosResponse> {
    return APIService.post('auth/get-qr-code', payload);
  }

  static switchMFA(payload: {
    accessToken: string;
    userCode: string;
    isEnable: boolean;
  }): Promise<AxiosResponse> {
    return APIService.post('auth/switch-mfa', payload);
  }

  // static enableMFA(payload: {
  //   accessToken: string;
  //   email: string;
  //   isEnableCustomAuthFlow?: boolean;
  // }): Promise<AxiosResponse> {
  //   const newPayload = {
  //     ...payload,
  //     isEnableCustomAuthFlow: !!payload.isEnableCustomAuthFlow,
  //   };
  //   return APIService.post('auth/enable-mfa', newPayload);
  // }

  // static disableMFA(payload: {
  //   email: string;
  //   userCode: string;
  //   userPoolId: string;
  //   accessToken: string;
  // }): Promise<AxiosResponse> {
  //   return APIService.post('auth/disable-mfa', payload);
  // }

  static getCaptcha(): Promise<AxiosResponse> {
    return APIService.get('auth/get-captcha');
  }

  static verifyOtp(payload: {
    id?: string;
    verifyValue?: string;
    codeOtp: string;
    email: string;
    session: string;
    clientId: string;
  }): Promise<AxiosResponse> {
    return APIService.post('auth/verify-otp', payload);
  }

  static enableCustomAuth(id: string): Promise<AxiosResponse> {
    return APIService.patch(`auth/${id}/enable-custom-auth`);
  }

  static disableCustomAuth(payload: {
    id: string;
    code: string;
  }): Promise<AxiosResponse> {
    return APIService.patch(`auth/${payload.id}/disable-custom-auth`, {
      code: payload.code,
    });
  }

  static sendEmailOtp(id: string): Promise<AxiosResponse> {
    return APIService.post(`auth/${id}/send-otp`);
  }

  static signIn(payload: SignInPayload): Promise<any> {
    const tenantData = newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]);

    const tenant = get(tenantData, 'data', {}) as TenantEntity;

    const { id: tenantId } = tenant;

    return axios.post(baseUrl + '/auth/sign-in', payload, {
      params: { tenantId },
    });
  }

  static magicLogin(
    payload: MagicLoginPayload,
  ): Promise<AxiosResponse<MagicLoginResponse>> {
    return axios.post(baseUrl + '/auth/verify-magic-link', payload);
  }

  static signOutWithoutResolve = () => postSignOut();

  static async signOut(): Promise<AxiosResponse<any, any>> {
    return postSignOut().finally(() => clearAuthStorage());
  }

  static async getNewTokens(): Promise<any> {
    const oldToken = getTokens();
    const { refreshToken } = oldToken;

    const tenantData = newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]);

    const tenant = get(tenantData, 'data', {}) as TenantEntity;

    const { id: tenantId } = tenant;

    return axios.post(
      baseUrl + '/auth/refresh-token',
      { refreshToken },
      { params: { tenantId } },
    );
  }

  static async changePassword(payload: {
    previousPassword: string;
    proposedPassword: string;
    accessToken?: string;
  }): Promise<void> {
    const { previousPassword, proposedPassword } = payload;
    const accessToken = getToken('accessToken') || payload?.accessToken || '';

    const tenantData = newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]);

    const tenant = get(tenantData, 'data', {}) as TenantEntity;

    const { id: tenantId } = tenant;

    return axios.post(
      baseUrl + '/auth/change-password',
      {
        accessToken,
        previousPassword,
        proposedPassword,
      },
      { params: { tenantId } },
    );
  }

  static async resetPasswordChallenge(payload: {
    password: string;
    token: string;
    subdomain: string;
  }) {
    return axios.post(baseUrl + '/auth/challenge-reset-password', payload);
  }

  static getSubdomain(): Promise<string> {
    return APIService.get('/auth/subdomain');
  }

  static initForgotPassword(payload: {
    email: string;
    tenantId: string;
  }): Promise<any> {
    return APIService.post('/auth/init-forgot-password', payload);
  }

  static confirmForgotPassword(payload: {
    email: string;
    verificationCode: string;
    password: string;
    tenantId: string;
  }): Promise<any> {
    return APIService.post('/auth/confirm-forgot-password', payload);
  }
}
